import React, { useEffect, useRef } from 'react';
import IconSVG from './IconSVG';

interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
}

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  items: MenuItem[];
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  className?: string;
}

const Menu: React.FC<MenuProps> = ({
  isOpen,
  onClose,
  title,
  items,
  position = 'bottom-right',
  className = '',
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close menu when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Get position classes based on position prop
  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-right':
        return 'top-full right-0';
      case 'bottom-left':
        return 'top-full left-0';
      case 'top-right':
        return 'bottom-full right-0';
      case 'top-left':
        return 'bottom-full left-0';
      default:
        return 'top-full right-0';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      onClick={onClose}
    >
      <div
        ref={menuRef}
        className={`absolute z-50 min-w-[200px] bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden ${getPositionClasses()} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="py-2 px-4 font-medium text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
            {title}
          </div>
        )}

        <div className="py-1">
          {items.map((item) => (
            <button
              key={item.id}
              className={`w-full text-left px-4 py-2 text-sm flex items-center
                ${item.disabled
                  ? 'text-gray-400 dark:text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                }
              }}
              disabled={item.disabled}
            >
              {item.icon && (
                <span className="mr-3 text-gray-500 dark:text-gray-400">
                  <IconSVG icon={item.icon} className="w-5 h-5" />
                </span>
              )}
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Menu;